<template>
  <ThePage :title="i18n(translations.pageTitle)">
    <template #pageTitle>
      {{ i18n(translations.title) }}
    </template>

    <template #pageContent>
      <RevTabs
        class="mb-32"
        :label="i18n(translations.navigation)"
        scrollable
        variant="flexible"
      >
        <RevTabItem
          v-for="tab in tabs"
          :key="tab.label"
          :active="currentTab === tab.name"
          :data-qa="tab.name"
          :label="tab.label"
          :to="getTabLink(tab.name)"
          @click="trackPageView(tab.name)"
        />
      </RevTabs>

      <Home v-if="currentTab === TAB_HOME" :insights />

      <Tableau
        v-if="insightToDisplay"
        :key="currentTab"
        :insight="insightToDisplay"
      />

      <Help v-if="currentTab !== TAB_HOME" :current-tab />
    </template>
  </ThePage>
</template>

<script lang="ts" setup>
import { useAsyncData, useRoute } from '#imports'
import { computed, watch } from 'vue'

import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { RevTabItem } from '@ds/components/TabItem'
import { RevTabs } from '@ds/components/Tabs'

import type { Insight } from '~/api/insights/types'
import { ROUTE_NAMES } from '~/constants/routes'

import { TAB_HOME } from './Insights.constants'
import translations from './Insights.translations'
import Help from './components/help/Help.vue'
import Tableau from './components/tableau/Tableau.vue'
import { useTabs } from './composables/useTabs'
import Home from './tabs/home/Home.vue'

const i18n = useI18n()
const route = useRoute()
const { openErrorToast } = useTheToast()
const tracking = useTracking()
const $httpFetch = useHttpFetch()

const { data: insights, error } = useAsyncData(
  () => $httpFetch('/api/seller-experience/insights'),
  { default: () => [] as Insight[] },
)

const { tabs, currentTab, goToTab, getTabLink } = useTabs(insights)

watch(
  () => error.value,
  (err) => {
    if (err) {
      openErrorToast()
    }
  },
)

const currentTabInsight = computed(() =>
  insights.value.find(({ name }) => name === currentTab.value),
)

const insightToDisplay = computed(() => {
  /**
   * An Insight can have a/some subview(s).
   * If it's the case, then the tableauViewParams to take is the one from
   * the subview
   */
  const subviews = currentTabInsight.value?.subviews
  if (route.query.subview && subviews) {
    const subInsight = subviews.find(({ name }) => name === route.query.subview)

    if (subInsight && subInsight.tableauViewParams) {
      return subInsight.tableauViewParams
    }
  }

  return currentTabInsight.value?.tableauViewParams
})

/**
 * Since we're not actually navigating to new routes but changing tabs within the same page,
 * based on the backend response, we need to manually trigger page_viewed events to track tab changes
 */
function trackPageView(tabName: string) {
  tracking.trackPageView({
    to: { ...route, params: { ...route.params, tableau: tabName } },
    from: route,
  })
}

watch(
  () => route.query.tableau,
  (tabName) => {
    if (route.name === ROUTE_NAMES.INSIGHTS.INDEX && !tabName) {
      goToTab(TAB_HOME)
    }
  },
  { immediate: true },
)
</script>
